import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { setAuthToken } from './services/api';
import { useToast } from './hooks/useToast';
import { APP_VERSION } from './version';

// Pages
import Landing from './pages/Landing';
import GuestView from './pages/GuestView';
import PlayerDashboard from './pages/PlayerDashboard';
import Dashboard from './pages/Dashboard';
import ServerList from './pages/ServerList';
import ServerDetail from './pages/ServerDetail';
import ModManager from './pages/ModManager';
import Players from './pages/Players';
import Backups from './pages/Backups';
import Modpacks from './pages/Modpacks';
import UserManagement from './pages/UserManagement';
import RulesEditor from './pages/RulesEditor';
import FeedbackViewer from './pages/FeedbackViewer';
import SkinLibrary from './pages/SkinLibrary';
import SkinEditor from './pages/SkinEditor';
import TexturePackLibrary from './pages/TexturePackLibrary';
import TextureEditor from './pages/TextureEditor';
import WorldGenerator from './pages/WorldGenerator';
import FeedbackButton from './components/FeedbackButton';
import SystemMetrics from './components/SystemMetrics';
import HelpButton from './components/HelpButton';

export const ToastContext = createContext();
export const useToastContext = () => useContext(ToastContext);

function AppContent() {
  const { user, token, loading, isAuthenticated, isGuest, isPlayer, isAdmin, logout } = useAuth();
  const { toasts, addToast } = useToast();
  const [sidebarOpen] = useState(true);

  // Keep api token in sync with auth state
  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  // Set document title with version
  useEffect(() => {
    document.title = `LexWar's Minecraft Versteck v${APP_VERSION}`;
  }, []);

  if (loading) {
    return (
      <div className="app-layout" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="spinner" />
      </div>
    );
  }

  // Not authenticated - show landing page
  if (!isAuthenticated) {
    return (
      <ToastContext.Provider value={addToast}>
        <Landing />
        <div className="toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`toast ${t.type}`}>{t.message}</div>
          ))}
        </div>
      </ToastContext.Provider>
    );
  }

  // Guest view
  if (isGuest) {
    return (
      <ToastContext.Provider value={addToast}>
        <GuestView />
        <FeedbackButton />
        <div className="toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`toast ${t.type}`}>{t.message}</div>
          ))}
        </div>
      </ToastContext.Provider>
    );
  }

  // Player view - with router for creative tools
  if (isPlayer) {
    return (
      <ToastContext.Provider value={addToast}>
        <BrowserRouter>
          <PlayerDashboard />
          <FeedbackButton />
          <div className="toast-container">
            {toasts.map(t => (
              <div key={t.id} className={`toast ${t.type}`}>{t.message}</div>
            ))}
          </div>
        </BrowserRouter>
      </ToastContext.Provider>
    );
  }

  // Admin view - full interface
  return (
    <ToastContext.Provider value={addToast}>
      <BrowserRouter>
        <div className="app-layout">
          {sidebarOpen && (
            <aside className="sidebar">
              <div className="sidebar-header">
                <h1>LexWar's MC Versteck</h1>
              </div>
              <nav className="sidebar-nav">
                <NavLink to="/" end className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#9776;</span>
                  Dashboard
                </NavLink>
                <NavLink to="/servers" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#127981;</span>
                  Server
                </NavLink>
                <NavLink to="/mods" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#9881;</span>
                  Mods
                </NavLink>
                <NavLink to="/modpacks" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#128230;</span>
                  Modpacks
                </NavLink>
                <NavLink to="/players" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#128100;</span>
                  Spieler
                </NavLink>
                <NavLink to="/backups" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#128190;</span>
                  Backups
                </NavLink>
                <NavLink to="/users" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#128101;</span>
                  Benutzer
                </NavLink>
                <NavLink to="/rules" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#128220;</span>
                  Regeln
                </NavLink>
                <NavLink to="/feedback" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#128172;</span>
                  Feedback
                </NavLink>
                <div className="sidebar-section-label">Kreativ-Werkzeuge</div>
                <NavLink to="/worldgen" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#127758;</span>
                  Weltgenerator
                </NavLink>
                <NavLink to="/skins" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#128100;</span>
                  Skin Editor
                </NavLink>
                <NavLink to="/texture-packs" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                  <span className="icon">&#127912;</span>
                  Texture Packs
                </NavLink>
              </nav>
              <div className="sidebar-footer">
                <div className="admin-user-info">
                  <span className="text-sm">&#128736; {user?.username || 'Admin'}</span>
                  <button className="btn btn-secondary btn-sm" onClick={logout}>Abmelden</button>
                </div>
              </div>
            </aside>
          )}

          <main className="main-content">
            {/* System Metrics — inline bar at top */}
            <SystemMetrics />
            <HelpButton />
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/servers" element={<ServerList />} />
              <Route path="/servers/:id" element={<ServerDetail />} />
              <Route path="/mods" element={<ModManager />} />
              <Route path="/modpacks" element={<Modpacks />} />
              <Route path="/players" element={<Players />} />
              <Route path="/backups" element={<Backups />} />
              <Route path="/users" element={<UserManagement />} />
              <Route path="/rules" element={<RulesEditor />} />
              <Route path="/feedback" element={<FeedbackViewer />} />
              <Route path="/skins" element={<SkinLibrary />} />
              <Route path="/skins/editor" element={<SkinEditor />} />
              <Route path="/skins/editor/:id" element={<SkinEditor />} />
              <Route path="/texture-packs" element={<TexturePackLibrary />} />
              <Route path="/texture-packs/:id" element={<TextureEditor />} />
              <Route path="/worldgen" element={<WorldGenerator />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          <div className="toast-container">
            {toasts.map(t => (
              <div key={t.id} className={`toast ${t.type}`}>{t.message}</div>
            ))}
          </div>
          <FeedbackButton />
        </div>
      </BrowserRouter>
    </ToastContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
